import React, { useMemo } from 'react'
import { Typography } from '@material-tailwind/react'
import { Source, Layer, Marker, LayerProps } from 'react-map-gl'
import Supercluster from 'supercluster'
import { MarkerEvent } from 'react-map-gl/dist/esm/types'
import { useAgamiMarkerStyle } from '../hooks/useAgamiMarkerStyle'
import { Position } from '../types/geoXp'

export type SuperclusterPositionProperties = {
  position: {
    id: string
    radius: number
    deadband: number
    label?: string
    description?: string
    lat: number
    lon: number
  }
  pattern: Position
}

export type SuperclusterClusterProperties = {
  cluster: boolean
  cluster_id: number
  point_count: number
}

export type SuperclusterFeatureProperties = Partial<SuperclusterPositionProperties> &
  Partial<SuperclusterClusterProperties>

export type SuperclusterFeature = {
  id: string | number
  type: 'Feature'
  geometry: {
    type: 'Point'
    coordinates: [number, number]
  }
  properties: SuperclusterFeatureProperties
}

interface XpClusterLayerProps {
  feature: Supercluster.PointFeature<SuperclusterClusterProperties>
  superCluster: Supercluster<SuperclusterFeatureProperties, Supercluster.AnyProps>
  onClick?: ((e: MarkerEvent<mapboxgl.Marker, MouseEvent>) => void) | undefined
}

const XpClusterLayer: React.FC<XpClusterLayerProps> = ({ feature, superCluster, onClick }) => {
  const { point_count: count, cluster_id: clusterId } = feature.properties
  const { clusterStyle: circleStyle } = useAgamiMarkerStyle()

  const patterns = useMemo(() => {
    const leaves = superCluster?.getLeaves(clusterId, Infinity)
    return leaves?.reduce((result: Position[], leaf) => {
      if (!leaf.properties.pattern) return result
      if (result.find((pattern) => pattern.id === leaf.properties.pattern?.id)) return result
      return [...result, leaf.properties.pattern]
    }, [])
  }, [])

  const circleSize = Math.min(Math.max(count * 2, 30), 100)

  const circleLayerStyle = useMemo(
    () =>
      ({
        id: `${feature.id}_circle`,
        type: 'circle',
        paint: {
          'circle-radius': circleSize,
          'circle-stroke-color':
            patterns.length === 1 && patterns[0]?.color ? patterns[0].color : circleStyle.color,
          'circle-stroke-opacity': circleStyle.opacity,
          'circle-stroke-width': circleStyle.weight,
          'circle-color':
            patterns.length === 1 && patterns[0]?.color ? patterns[0].color : circleStyle.fillColor,
          'circle-opacity': circleStyle.fillOpacity,
          'circle-pitch-alignment': 'map',
        },
      }) as LayerProps,
    [patterns]
  )

  return (
    <>
      <Source id={`${feature.id}_source`} key={`${feature.id}_source`} type="geojson" data={feature}>
        <Layer {...circleLayerStyle} />
      </Source>
      <Marker
        longitude={feature.geometry.coordinates[0]!}
        latitude={feature.geometry.coordinates[1]!}
        anchor="center"
        onClick={onClick}
      >
        <div className="bg-transparent relative text-center" style={{ width: circleSize }}>
          <Typography className="font-bold">{count}</Typography>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full flex flex-wrap gap-2 justify-center w-full">
            {patterns.length > 1 &&
              patterns
                .filter((pattern) => pattern.id !== 'default')
                .map((pattern) => (
                  <div
                    key={pattern.id}
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: pattern.color }}
                  />
                ))}
          </div>
        </div>
      </Marker>
    </>
  )
}

export default XpClusterLayer
